import { GlobalSettingInstance, getInitialSettings } from "./GlobalSettingStore";
import { createContext, use } from "react";

import { ApiServiceStore } from "./ApiServiceStore";
import { AppSettingStore } from "./AppSettingStore";
import { IdentityApiServiceStore as CreateIdentityApiServiceStore } from "./IdentityApiServiceStore";
import { AuthStore } from "./AuthStore";
import { DateTime } from "luxon";
import { LogStore } from "./LogStore";
import { MyStore } from "./MyStore";
import { UniversalSearchStore } from "./UniversalSearchStore";
import { oneDsAnalystic } from "./OneDsAnalystic";
import { runInAction } from "mobx";

export const RootStore = () => {
    let settingStorageKey = "GlobalSettingV2";

    let appSettingStore: ReturnType<typeof AppSettingStore> | undefined;
    let logStore: ReturnType<typeof LogStore> | undefined;
    let apiServiceStore: ReturnType<typeof ApiServiceStore> | undefined;
    let identityApiServiceStore: ReturnType<typeof ApiServiceStore> | undefined;
    let elipApiServiceStore: ReturnType<typeof ApiServiceStore> | undefined;
    let globalSettingStore: GlobalSettingInstance | undefined;
    const siteToasterId = "siteToaster";
    const IncidentAppWorkloadFilterId = "IncidentAppWorkloadFilter";
    const RulesAppWorkloadFilterId = "RulesAppWorkloadFilter";
    const ScenariosAppWorkloadFilterId = "ScenariosAppWorkloadFilter";
    const loadingTime = DateTime.now();

    return {
        isInit: false,
        MyStore: MyStore(),
        AuthStore: AuthStore(),
        UniversalSearchStore: UniversalSearchStore(),
        get AppSettingStore() {
            if (!appSettingStore) {
                appSettingStore = AppSettingStore(this);
            }
            return appSettingStore;
        },
        get LogStore() {
            if (!logStore) {
                logStore = LogStore(this.AppSettingStore, this.MyStore);
            }
            return logStore;
        },
        get ApiServiceStore() {
            if (!apiServiceStore) {
                apiServiceStore = ApiServiceStore(this);
            }
            return apiServiceStore;
        },
        get IdentityApiServiceStore() {
            if (!identityApiServiceStore) {
                identityApiServiceStore = CreateIdentityApiServiceStore(this);
            }
            return identityApiServiceStore;
        },
        get ElipApiServiceStore() {
            if (!elipApiServiceStore) {
                elipApiServiceStore = ApiServiceStore(this, "elipServiceApiUrl");
            }
            return elipApiServiceStore;
        },
        get GlobalSetting() {
            if (!globalSettingStore) {
                globalSettingStore = getInitialSettings(settingStorageKey);
            }
            return globalSettingStore;
        },
        get SettingStorageKey() {
            return settingStorageKey;
        },
        async init(configuredSettingStorageKey?: string) {
            if (this.isInit) {
                return;
            }
            oneDsAnalystic();
            if (configuredSettingStorageKey) {
                settingStorageKey = configuredSettingStorageKey;
            }
            await this.AppSettingStore.init();
            this.LogStore.initialize();
            this.ApiServiceStore.initialize();
            this.IdentityApiServiceStore.initialize();
            this.ElipApiServiceStore.initialize();
            runInAction(() => {
                this.isInit = true;
            });
        },
        get toasterId() {
            return siteToasterId;
        },
        get IncidentAppWorkloadFilterId() {
            return IncidentAppWorkloadFilterId;
        },
        get ScenariosAppWorkloadFilterId() {
            return ScenariosAppWorkloadFilterId;
        },
        get RulesAppWorkloadFilterId() {
            return RulesAppWorkloadFilterId;
        },
        get LoadingTime() {
            return loadingTime;
        }
    };
};

export type TRootStore = ReturnType<typeof RootStore>;

const rootContext = createContext<TRootStore | null>(null);
export const RootContextProvider = rootContext;
export const useRootStore = <T extends keyof TRootStore>(store: T): TRootStore[T] => {
    const context = use(rootContext);
    if (context === null) {
        throw new Error("useRootStore must be used within RootContextProvider");
    }
    return context[store];
};
